import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, Calculator, UserPlus, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const DevisEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [prestations, setPrestations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newClientMode, setNewClientMode] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  
  const [newClient, setNewClient] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    code_postal: '',
    ville: '',
    type: 'particulier'
  });
  
  const [formData, setFormData] = useState({
    client_id: '',
    event_info: {
      event_type: 'mariage',
      vedettes: '',
      dates: [],
      nb_invites: '',
      has_vin_honneur: false,
      has_henne: false,
      prestation_type: 'photo_video',
      comments: ''
    },
    lines: [],
    discount_total_percent: 0,
    acompte_percent: 30,
    acompte_mode: 'percent',
    acompte_euros: 0,
    include_tva: false,
    date_evenement_principal: ''
  });

  const [calculated, setCalculated] = useState({
    sous_total_ht: 0,
    discount_total_euros: 0,
    total_ht: 0,
    tva_amount: 0,
    total_ttc: 0,
    acompte_euros: 0
  });

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    calculateTotals();
  }, [formData.lines, formData.discount_total_percent, formData.acompte_percent, formData.acompte_mode, formData.acompte_euros, formData.include_tva]);

  const checkDuplicate = async (email, telephone) => {
    if (!email && !telephone) return;
    
    try {
      const res = await axios.post(`${API}/clients/check-duplicate`, { email, telephone });
      if (res.data.exists) {
        setDuplicateWarning(res.data.client);
      } else {
        setDuplicateWarning(null);
      }
    } catch (error) {
      console.error('Error checking duplicate:', error);
    }
  };

  const handleNewClientChange = (field, value) => {
    const updated = { ...newClient, [field]: value };
    setNewClient(updated);
    
    if (field === 'email' || field === 'telephone') {
      checkDuplicate(updated.email, updated.telephone);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [clientsRes, prestasRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/clients`, { withCredentials: true }),
        axios.get(`${API}/prestations`),
        axios.get(`${API}/prestation-categories`)
      ]);
      setClients(clientsRes.data);
      setPrestations(prestasRes.data);
      setCategories(categoriesRes.data);

      if (isEditing) {
        const devisRes = await axios.get(`${API}/devis/${id}`, { withCredentials: true });
        const devis = devisRes.data;
        // The dates now come from the relation, not the JSON field.
        // We also need to format the date part for the HTML input.
        const dates = (devis.dates || []).map(d => ({
          ...d,
          date: d.date ? d.date.split('T')[0] : '' // Format to YYYY-MM-DD
        }));
        
        setFormData({
          client_id: devis.client_id,
          event_info: {
            ...devis.event_info,
            dates: dates // Use the formatted dates from the relation
          },
          lines: devis.lines,
          discount_total_percent: devis.discount_total_percent,
          acompte_percent: devis.acompte_percent,
          acompte_mode: devis.acompte_mode || 'percent',
          acompte_euros: devis.acompte_euros,
          include_tva: devis.include_tva || false,
          date_evenement_principal: devis.date_evenement_principal ? devis.date_evenement_principal.split('T')[0] : ''
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    let sousTotal = 0;
    formData.lines.forEach(line => {
      let lineTotal = line.price_ht * line.quantity;
      if (line.discount_percent > 0) {
        lineTotal = lineTotal * (1 - line.discount_percent / 100);
      }
      sousTotal += lineTotal;
    });

    const discountEuros = sousTotal * formData.discount_total_percent / 100;
    const totalHt = sousTotal - discountEuros;
    const tva = formData.include_tva ? totalHt * 0.20 : 0;
    const totalTtc = totalHt + tva;
    
    let acompteEuros;
    if (formData.acompte_mode === 'percent') {
      acompteEuros = totalTtc * formData.acompte_percent / 100;
    } else {
      acompteEuros = formData.acompte_euros;
    }

    setCalculated({
      sous_total_ht: Math.round(sousTotal * 100) / 100,
      discount_total_euros: Math.round(discountEuros * 100) / 100,
      total_ht: Math.round(totalHt * 100) / 100,
      tva_amount: Math.round(tva * 100) / 100,
      total_ttc: Math.round(totalTtc * 100) / 100,
      acompte_euros: Math.round(acompteEuros * 100) / 100
    });
  };

  const addLine = (prestation) => {
    const newLine = {
      prestation_id: prestation.id,
      prestation_name: prestation.name,
      description: prestation.description,
      price_ht: prestation.price_ht,
      discount_percent: 0,
      quantity: 1,
      cost: prestation.cost
    };
    setFormData(prev => ({ ...prev, lines: [...prev.lines, newLine] }));
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setFormData(prev => ({ ...prev, lines: newLines }));
  };

  const removeLine = (index) => {
    setFormData(prev => ({ ...prev, lines: prev.lines.filter((_, i) => i !== index) }));
  };

  const addDate = () => {
    const newDate = {
      date: '',
      heure: '',
      lieu: '',
      type: '',
      adresse: ''
    };
    setFormData(prev => ({
      ...prev,
      event_info: {
        ...prev.event_info,
        dates: [...(prev.event_info.dates || []), newDate]
      }
    }));
  };

  const updateDate = (index, field, value) => {
    const newDates = [...(formData.event_info.dates || [])];
    newDates[index] = { ...newDates[index], [field]: value };
    setFormData(prev => ({
      ...prev,
      event_info: { ...prev.event_info, dates: newDates }
    }));
  };

  const removeDate = (index) => {
    setFormData(prev => ({
      ...prev,
      event_info: {
        ...prev.event_info,
        dates: (prev.event_info.dates || []).filter((_, i) => i !== index)
      }
    }));
  };

  const getLineTotal = (line) => {
    let total = line.price_ht * line.quantity;
    if (line.discount_percent > 0) {
      total = total * (1 - line.discount_percent / 100);
    }
    return Math.round(total * 100) / 100;
  };

  const formatPrice = (price, discount) => {
    if (discount === 100) return 'OFFERT';
    return `${price.toLocaleString()}€`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!newClientMode && !formData.client_id) {
      toast.error('Veuillez sélectionner ou créer un client');
      return;
    }
    
    if (newClientMode) {
      if (!newClient.nom || !newClient.prenom || !newClient.email || !newClient.telephone) {
        toast.error('Veuillez remplir tous les champs obligatoires du client');
        return;
      }
      
      if (duplicateWarning) {
        toast.error('Ce client existe déjà. Utilisez la sélection de client existant.');
        return;
      }
    }
    
    if (formData.lines.length === 0) {
      toast.error('Ajoutez au moins une prestation');
      return;
    }

    setLoading(true);
    try {
      let clientId = formData.client_id;
      
      // Create client if in new client mode
      if (newClientMode && !isEditing) {
        const clientRes = await axios.post(`${API}/clients`, newClient, { withCredentials: true });
        clientId = clientRes.data.id;
      }
      
      // Prepare data for submission, ensuring event_info does not contain dates
      const { dates, ...eventInfoClean } = formData.event_info;
      const dataToSubmit = {
        ...formData,
        client_id: clientId,
        event_info: eventInfoClean, // Send event_info without dates
        dates: dates // Send dates at the top level
      };
      
      if (isEditing) {
        await axios.put(`${API}/devis/${id}`, dataToSubmit, { withCredentials: true });
        toast.success('Devis mis à jour');
        // Stay on the same page when editing
      } else {
        const response = await axios.post(`${API}/devis`, dataToSubmit, { withCredentials: true });
        toast.success('Devis créé');
        // Navigate to edit page for the newly created devis
        navigate(`/backoffice/devis/${response.data.id}/edit`);
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" onClick={() => navigate('/backoffice/devis')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-stone-800">
            {isEditing ? 'Modifier le devis' : 'Nouveau devis'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Client & Event Info */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Client</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!isEditing && (
                <div className="flex gap-4 mb-4">
                  <Button
                    type="button"
                    variant={!newClientMode ? 'default' : 'outline'}
                    onClick={() => setNewClientMode(false)}
                    size="sm"
                  >
                    Sélectionner existant
                  </Button>
                  <Button
                    type="button"
                    variant={newClientMode ? 'default' : 'outline'}
                    onClick={() => setNewClientMode(true)}
                    size="sm"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Créer nouveau
                  </Button>
                </div>
              )}

              {!newClientMode ? (
                <div>
                  <Label>Sélectionner un client *</Label>
                  <Select value={formData.client_id} onValueChange={(v) => setFormData(prev => ({ ...prev, client_id: v }))}>
                    <SelectTrigger data-testid="select-client">
                      <SelectValue placeholder="Choisir un client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.prenom} {client.nom} ({client.type}) - {client.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-3">
                  {duplicateWarning && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Ce client existe déjà : {duplicateWarning.prenom} {duplicateWarning.nom}
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm">Prénom *</Label>
                      <Input
                        value={newClient.prenom}
                        onChange={(e) => handleNewClientChange('prenom', e.target.value)}
                        required
                        size="sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Nom *</Label>
                      <Input
                        value={newClient.nom}
                        onChange={(e) => handleNewClientChange('nom', e.target.value)}
                        required
                        size="sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Email *</Label>
                      <Input
                        type="email"
                        value={newClient.email}
                        onChange={(e) => handleNewClientChange('email', e.target.value)}
                        required
                        size="sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Téléphone *</Label>
                      <Input
                        value={newClient.telephone}
                        onChange={(e) => handleNewClientChange('telephone', e.target.value)}
                        required
                        size="sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-sm">Adresse</Label>
                      <Input
                        value={newClient.adresse}
                        onChange={(e) => handleNewClientChange('adresse', e.target.value)}
                        size="sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Code Postal</Label>
                      <Input
                        value={newClient.code_postal}
                        onChange={(e) => handleNewClientChange('code_postal', e.target.value)}
                        size="sm"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Ville</Label>
                      <Input
                        value={newClient.ville}
                        onChange={(e) => handleNewClientChange('ville', e.target.value)}
                        size="sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-sm">Type</Label>
                      <Select
                        value={newClient.type}
                        onValueChange={(v) => handleNewClientChange('type', v)}
                      >
                        <SelectTrigger size="sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="particulier">Particulier</SelectItem>
                          <SelectItem value="partenaire">Partenaire</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Événement</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type d'événement</Label>
                  <Select 
                    value={formData.event_info.event_type} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, event_info: { ...prev.event_info, event_type: v } }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mariage">Mariage</SelectItem>
                      <SelectItem value="barmitzvah">Bar/Bat Mitsvah</SelectItem>
                      <SelectItem value="brit_mila">Brit Mila</SelectItem>
                      <SelectItem value="coupe_cheveux">Coupe de Cheveux</SelectItem>
                      <SelectItem value="henne">Henné</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                    <Label>Date événement principal</Label>
                    <Input
                        type="date"
                        value={formData.date_evenement_principal || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, date_evenement_principal: e.target.value }))}
                    />
                </div>
              </div>
              <div>
                  <Label>Vedettes</Label>
                  <Input
                    value={formData.event_info.vedettes}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_info: { ...prev.event_info, vedettes: e.target.value } }))}
                    placeholder="Ex: Sarah & David"
                    data-testid="input-vedettes"
                  />
              </div>
              <div>
                <Label>Commentaires</Label>
                <Textarea
                  value={formData.event_info.comments}
                  onChange={(e) => setFormData(prev => ({ ...prev, event_info: { ...prev.event_info, comments: e.target.value } }))}
                  placeholder="Notes additionnelles..."
                />
              </div>

              {/* Dates de l'événement */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-base font-semibold">Dates et lieux de l'événement</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addDate}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter une date
                  </Button>
                </div>

                {(!formData.event_info.dates || formData.event_info.dates.length === 0) ? (
                  <p className="text-sm text-stone-500 text-center py-4">Aucune date ajoutée</p>
                ) : (
                  <div className="space-y-4">
                    {formData.event_info.dates.map((dateInfo, index) => (
                      <Card key={index} className="border-stone-200">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-4">
                            <h4 className="font-medium text-stone-800">Date {index + 1}</h4>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeDate(index)}
                            >
                              <Trash2 className="h-4 w-4 text-rose-500" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs">Date *</Label>
                              <Input
                                type="date"
                                value={dateInfo.date || ''}
                                onChange={(e) => updateDate(index, 'date', e.target.value)}
                                required
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Heure</Label>
                              <Input
                                type="time"
                                value={dateInfo.heure || ''}
                                onChange={(e) => updateDate(index, 'heure', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Type</Label>
                              <Input
                                value={dateInfo.type || ''}
                                onChange={(e) => updateDate(index, 'type', e.target.value)}
                                placeholder="Ex: Mairie, Synagogue, Réception"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Lieu</Label>
                              <Input
                                value={dateInfo.lieu || ''}
                                onChange={(e) => updateDate(index, 'lieu', e.target.value)}
                                placeholder="Nom du lieu"
                              />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Adresse</Label>
                              <Input
                                value={dateInfo.adresse || ''}
                                onChange={(e) => updateDate(index, 'adresse', e.target.value)}
                                placeholder="Adresse complète"
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Prestations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Prestations</span>
              <Select onValueChange={(v) => {
                const presta = prestations.find(p => p.id === v);
                if (presta) addLine(presta);
              }}>
                <SelectTrigger className="w-64">
                  <Plus className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Ajouter une prestation" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => {
                    const catPrestations = prestations.filter(p => {
                      // Vérifier à la fois category et prestation_type, insensible à la casse
                      const categoryMatch = p.category && p.category.toLowerCase() === category.name.toLowerCase();
                      const prestationTypeMatch = p.prestation_type && p.prestation_type.toLowerCase() === category.name.toLowerCase();
                      return categoryMatch || prestationTypeMatch;
                    });
                    if (catPrestations.length === 0) return null;
                    return (
                      <React.Fragment key={category.id}>
                        <div className="px-2 py-1 text-xs font-semibold text-stone-500 uppercase">
                          {category.emoji} {category.name}
                        </div>
                        {catPrestations.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} - {p.price_ht}€ HT
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </SelectContent>
              </Select>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {formData.lines.length === 0 ? (
              <p className="text-center text-stone-500 py-8">Aucune prestation ajoutée</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prestation</TableHead>
                    <TableHead className="w-24">Prix HT</TableHead>
                    <TableHead className="w-24">Remise %</TableHead>
                    <TableHead className="w-20">Qté</TableHead>
                    <TableHead className="w-28 text-right">Total HT</TableHead>
                    <TableHead className="w-16"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.lines.map((line, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="font-medium">{line.prestation_name}</div>
                        <div className="text-sm text-stone-500">{line.description}</div>
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={line.price_ht}
                          onChange={(e) => updateLine(index, 'price_ht', parseFloat(e.target.value) || 0)}
                          className="w-20"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={line.discount_percent}
                          onChange={(e) => updateLine(index, 'discount_percent', parseFloat(e.target.value) || 0)}
                          className="w-16"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          min="1"
                          value={line.quantity}
                          onChange={(e) => updateLine(index, 'quantity', parseInt(e.target.value) || 1)}
                          className="w-16"
                        />
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatPrice(getLineTotal(line), line.discount_percent)}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => removeLine(index)}>
                          <Trash2 className="h-4 w-4 text-rose-500" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Totaux */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Récapitulatif
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Remise & Acompte */}
              <div className="space-y-4">
                <div>
                  <Label>Remise globale (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.discount_total_percent}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount_total_percent: parseFloat(e.target.value) || 0 }))}
                    className="w-32"
                    data-testid="input-discount-percent"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include-tva"
                    checked={formData.include_tva}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_tva: !!checked }))}
                  />
                  <Label htmlFor="include-tva" className="cursor-pointer">
                    Inclure la TVA (20%)
                  </Label>
                </div>
                <div className="space-y-2">
                  <Label>Acompte</Label>
                  <div className="flex gap-4">
                    <div>
                      <Label className="text-xs text-stone-500">Mode</Label>
                      <Select value={formData.acompte_mode} onValueChange={(v) => setFormData(prev => ({ ...prev, acompte_mode: v }))}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percent">Pourcentage</SelectItem>
                          <SelectItem value="euros">Euros</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.acompte_mode === 'percent' ? (
                      <div>
                        <Label className="text-xs text-stone-500">Pourcentage</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={formData.acompte_percent}
                            onChange={(e) => setFormData(prev => ({ ...prev, acompte_percent: parseFloat(e.target.value) || 0 }))}
                            className="w-20"
                            data-testid="input-acompte-percent"
                          />
                          <span>%</span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Label className="text-xs text-stone-500">Montant</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0"
                            value={formData.acompte_euros}
                            onChange={(e) => setFormData(prev => ({ ...prev, acompte_euros: parseFloat(e.target.value) || 0 }))}
                            className="w-28"
                            data-testid="input-acompte-euros"
                          />
                          <span>€</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Totaux */}
              <div className="bg-stone-50 p-6 rounded-xl space-y-3">
                <div className="flex justify-between">
                  <span className="text-stone-600">Sous-total HT</span>
                  <span className="font-medium">{calculated.sous_total_ht.toLocaleString()}€</span>
                </div>
                {formData.discount_total_percent > 0 && (
                  <div className="flex justify-between text-amber-600">
                    <span>Remise ({formData.discount_total_percent}%)</span>
                    <span>-{calculated.discount_total_euros.toLocaleString()}€</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-stone-600">Total HT</span>
                  <span className="font-medium">{calculated.total_ht.toLocaleString()}€</span>
                </div>
                {formData.include_tva && (
                  <div className="flex justify-between">
                    <span className="text-stone-600">TVA (20%)</span>
                    <span>{calculated.tva_amount.toLocaleString()}€</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total {formData.include_tva ? 'TTC' : 'HT'}</span>
                  <span>{calculated.total_ttc.toLocaleString()}€</span>
                </div>
                <div className="flex justify-between text-amber-700 pt-2">
                  <span>Acompte ({formData.acompte_mode === 'percent' ? `${parseFloat(formData.acompte_percent).toFixed(1)}%` : `fixe (soit ${((calculated.acompte_euros / calculated.total_ttc) * 100).toFixed(1)}%)`})</span>
                  <span className="font-medium">{calculated.acompte_euros.toLocaleString()}€</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/backoffice/devis')}>
            Annuler
          </Button>
          <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={loading} data-testid="btn-save-devis">
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DevisEditor;
